import request from '@/utils/request'
import { ensureHttpsUrl, ensureHttpsUrls } from '@/utils/mediaUrl'
import { fetchResourcesByIds, mapResourceItem } from '@/api/resources'

function unwrapPage(res) {
  if (res && res.code && res.code !== 2000) {
    return Promise.reject(new Error(res.message || '请求失败'))
  }
  const page = (res && res.items) || res || {}
  return {
    records: page.Data || page.data || page.records || [],
    total: page.totalCount != null ? page.totalCount : (page.total || 0)
  }
}

function unwrapData(res) {
  if (res && res.code && res.code !== 2000) {
    return Promise.reject(new Error(res.message || '请求失败'))
  }
  return (res && res.items) != null ? res.items : res
}

export function searchKnowledgeEntities(params) {
  return request({
    url: '/pc/knowledge/entity/search',
    method: 'get',
    params
  }).then(unwrapPage)
}

export function fetchKnowledgeSpaceTime(params) {
  return request({
    url: '/pc/knowledge/spaceTime',
    method: 'get',
    params
  }).then(unwrapData)
}

export function fetchKnowledgeFacets(params) {
  return request({
    url: '/pc/knowledge/entity/facets',
    method: 'get',
    params
  }).then(unwrapData)
}

export function fetchKnowledgeEntityDetail(id) {
  return request({
    url: '/pc/knowledge/entity/detail',
    method: 'get',
    params: { id }
  }).then(unwrapData)
}

/** 根据实体 ID 查询关联实体（entity_relation_record） */
export function fetchKnowledgeRelatedEntities(entityId) {
  return request({
    url: '/pc/knowledge/entity/related-entities',
    method: 'get',
    params: { entityId }
  }).then(unwrapData).then((data) => (Array.isArray(data) ? data : []))
}

/**
 * 将 related-entities 接口数据适配为 Event 时间轴组件所需结构
 */
export function mapRelatedEntitiesToEventData(entities) {
  const INTRO_CODES = ['event_summary', 'jigoujianjie', 'didianjianjie', 'renwugaishu', 'summary', 'jianjie']

  const events = (entities || []).map((entity) => {
    const attributes = entity.attributes || []
    const attrByCode = {}
    attributes.forEach((attr) => {
      if (attr.code) {
        attrByCode[attr.code] = attr
      }
    })

    const textOf = (...codes) => {
      for (let i = 0; i < codes.length; i += 1) {
        const attr = attrByCode[codes[i]]
        if (attr && attr.value) {
          return String(attr.value).trim()
        }
      }
      return ''
    }

    // 时间轴标题与日期标签统一取 attributes 中 code=start_timed 的值
    const startTimed = textOf('start_time')
    const date = startTimed || ''

    const EventPicture = []
    attributes.forEach((attr) => {
      ;(attr.imageUrls || []).forEach((url) => {
        if (url) {
          EventPicture.push({ url: ensureHttpsUrl(url) })
        }
      })
    })

    return {
      id: entity.id,
      name: entity.name || '',
      year: startTimed || '-',
      date,
      eventSummary: textOf(...INTRO_CODES) || '-',
      EventPicture
    }
  })

  return {
    events,
    coordinate: buildEventCoordinate(events)
  }
}

function extractEventYear(text) {
  if (!text) {
    return ''
  }
  const match = String(text).match(/\d{4}/)
  return match ? match[0] : String(text).slice(0, 20)
}

function buildEventCoordinate(events) {
  const years = events
    .map((item) => parseInt(extractEventYear(item.date), 10))
    .filter((year) => Number.isFinite(year))
    .sort((a, b) => a - b)

  if (!years.length) {
    return []
  }

  const min = years[0]
  const max = years[years.length - 1]
  if (min === max) {
    return [`${min}-${max}`]
  }

  const segmentCount = Math.min(6, Math.max(2, Math.ceil(years.length / 3)))
  const step = Math.max(1, Math.ceil((max - min) / segmentCount))
  const ranges = []
  for (let start = min; start <= max; start += step) {
    const end = Math.min(start + step - 1, max)
    ranges.push(`${start}-${end}`)
    if (end >= max) {
      break
    }
  }
  return ranges
}

export function fetchKnowledgeEntityResources(params) {
  return request({
    url: '/pc/knowledge/entity/resources',
    method: 'get',
    params
  }).then(unwrapPage)
}

/** 根据实体 ID 分页查询关联资源关系（oms_entity_resource_rel） */
export function fetchEntityRelatedResourceRelations(params) {
  return request({
    url: '/pc/knowledge/entity/resource-relations',
    method: 'get',
    params
  }).then(unwrapPage)
}

/**
 * 根据实体 ID 获取关联资源列表：
 * 1. 查询实体关联的资源 ID
 * 2. 按资源 ID 批量获取资源（逻辑同 /pc/resource/search）
 */
export async function fetchEntityRelatedResources(params) {
  const { records, total } = await fetchEntityRelatedResourceRelations(params)
  const resourceIds = (records || [])
    .map((item) => item.resourceId)
    .filter(Boolean)

  if (!resourceIds.length) {
    return { records: [], total: total || 0 }
  }

  const resources = await fetchResourcesByIds(resourceIds)
  return {
    records: resources.map(mapResourceItem),
    total: total || 0
  }
}

export function fetchKnowledgeEntityGraph(entityId) {
  return request({
    url: '/pc/knowledge/entity/graph',
    method: 'get',
    params: entityId ? { entityId } : {}
  }).then(unwrapData)
}

export function fetchKnowledgeEntityAttribute(entityId) {
  return request({
    url: '/pc/knowledge/entityAttribute',
    method: 'get',
    params: { entityId }
  }).then(unwrapData)
}

const MEDIA_VALUE_RANGES = ['image', 'image_set', 'video_urls', 'audio_urls']

export function mapEntityAttribute(attr) {
  const valueRange = attr.valueRange || ''
  const isMedia = MEDIA_VALUE_RANGES.includes(valueRange)
  let value = attr.attributeValue || ''

  if (!isMedia && value) {
    value = formatAttributeTextValue(value, valueRange)
  }

  return {
    id: attr.id || attr.attributeId,
    label: attr.attributeName || '',
    value,
    valueRange,
    isMedia,
    imageUrls: isMedia
      ? ensureHttpsUrls((attr.attributeUrl || []).map((item) => item.url || item.origin).filter(Boolean))
      : []
  }
}

function formatAttributeTextValue(raw, valueRange) {
  if (!raw || raw === '[\"\"]') {
    return ''
  }
  if (valueRange === 'location_coordinates') {
    return formatLocationCoordinateValue(raw)
  }
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.filter(Boolean).join('，')
    }
  } catch (error) {
    // keep raw text
  }
  return String(raw).trim()
}

function formatLocationCoordinateValue(raw) {
  try {
    const parsed = JSON.parse(raw)
    const items = Array.isArray(parsed) ? parsed : [parsed]
    return items
      .map((item) => formatLocationCoordinateItem(item))
      .filter(Boolean)
      .join('，')
  } catch (error) {
    return String(raw).trim()
  }
}

function formatLocationCoordinateItem(item) {
  if (!item || typeof item !== 'object') {
    return ''
  }
  const lat = Number(item.lat != null ? item.lat : item.latitude)
  const lng = Number(item.lng != null ? item.lng : (item.lon != null ? item.lon : item.longitude))
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng) && (Math.abs(lat) > 1e-8 || Math.abs(lng) > 1e-8)
  if (!hasCoords) {
    return ''
  }
  const name = String(item.name || '').trim()
  return name ? `${name}（${lat}, ${lng}）` : `${lat}, ${lng}`
}

export function mapKnowledgeGraph(data, entityId) {
  const nodes = (data.nodes || []).map((node) => mapGraphNode(node))
  const nodeIdSet = new Set(nodes.map((node) => node.id))
  const relations = (data.relations || [])
    .map((relation) => ({
      id: String(relation.id),
      source: String(relation.startId),
      target: String(relation.endId),
      label: relation.typeName
        || relation.type
        || (relation.property && (relation.property.name || relation.property.typeName))
        || ''
    }))
    .filter((relation) => nodeIdSet.has(relation.source) && nodeIdSet.has(relation.target))
  const focusEntityId = entityId ? Number(entityId) : null
  const focusNodeId = data.centerNodeId != null ? String(data.centerNodeId) : null

  return {
    nodes,
    relations,
    focusEntityId,
    focusNodeId,
    hasData: nodes.length > 0
  }
}

function mapGraphNode(node) {
  const property = node.property || {}
  const labels = node.label || []
  return {
    id: String(node.id),
    neo4jId: node.id,
    name: property.name || '未命名',
    category: resolveGraphCategory(property, labels),
    entityId: property.entity_id != null ? Number(property.entity_id) : null,
    identityName: property.identity_name || property.identityName || '',
    labels,
    property
  }
}

function resolveGraphCategory(property, labels) {
  const labelText = (Array.isArray(labels) ? labels : [labels])
    .filter(Boolean)
    .join(',')
    .toLowerCase()

  // 接口 label 为本体编码：renwu_1 / rongshirenwu / jigou_1 / shijian_1 / didian_1
  if (labelText.includes('jigou')) return 'org'
  if (labelText.includes('shijian')) return 'event'
  if (labelText.includes('didian')) return 'location'
  if (labelText.includes('renwu') || labelText.includes('rongshi')) return 'person'

  const nameText = `${property.identity_name || ''}${property.parent_identity_name || ''}`
  if (nameText.includes('机构')) return 'org'
  if (nameText.includes('事件')) return 'event'
  if (nameText.includes('地点')) return 'location'
  if (nameText.includes('人物')) return 'person'
  return 'person'
}

export function mapKnowledgeItem(item) {
  return {
    id: item.id,
    type: normalizeTypeKey(item.parentIdentityCode || item.identityCode),
    typeLabel: item.identityName || item.parentIdentityName || '',
    title: item.name || '',
    image: item.image || '',
    intro: item.intro || '',
    meta: (item.attributes || []).map(mapKnowledgeAttribute)
  }
}

export function mapKnowledgeAttribute(attr) {
  return {
    id: attr.attributeId,
    label: attr.label || '',
    value: attr.value || ''
  }
}

export function mapKnowledgeDetail(item, routeType) {
  const attributes = (item.attributes || []).map(mapKnowledgeDetailAttribute)
  const type = normalizeTypeKey(routeType || item.parentIdentityCode || item.identityCode)
  const biography = extractBiography(attributes)
  const gallery = buildGallery(item.image, attributes)
  const fields = attributes
    .filter((attr) => !isMediaAttribute(attr))
    .map((attr) => ({
      code: attr.code,
      label: attr.label,
      value: attr.value && attr.value.trim() ? attr.value : '-',
      valueRange: attr.valueRange || '',
      resourceLinks: attr.resourceLinks || []
    }))
  const mediaAttributes = attributes
    .filter((attr) => isMediaAttribute(attr) && attr.imageUrls && attr.imageUrls.length)
    .map((attr) => ({
      label: attr.label,
      imageUrls: attr.imageUrls
    }))

  return {
    id: item.id,
    title: item.name || '',
    type,
    typeLabel: item.identityName || item.parentIdentityName || '',
    identityName: item.identityName || '',
    parentIdentityName: item.parentIdentityName || '',
    gallery,
    fields,
    mediaAttributes,
    biography: biography || '暂无介绍',
    introLabel: getIntroLabel(type),
    attributes
  }
}

const INTRO_CODES = ['biography']
const INTRO_RANGES = ['long_text', 'text']

function isBiographyAttribute(attr) {
  const code = (attr.code || '').toLowerCase()
  const range = (attr.valueRange || '').toLowerCase()
  return INTRO_CODES.includes(code) || INTRO_RANGES.includes(range)
}

function extractBiography(attributes) {
  const introAttr = attributes.find(
    (attr) => isBiographyAttribute(attr) && attr.value && attr.value.trim()
  )
  return introAttr ? introAttr.value.trim() : ''
}

function buildGallery(coverImage, attributes) {
  const urls = []
  if (coverImage) {
    urls.push(ensureHttpsUrl(coverImage))
  }
  attributes.forEach((attr) => {
    if (!isMediaAttribute(attr) || !attr.imageUrls) {
      return
    }
    attr.imageUrls.forEach((url) => {
      const secureUrl = ensureHttpsUrl(url)
      if (secureUrl && !urls.includes(secureUrl)) {
        urls.push(secureUrl)
      }
    })
  })
  return urls
}

function getIntroLabel(type) {
  const labels = {
    renwu: '人物介绍',
    person: '人物介绍',
    jigou: '机构介绍',
    org: '机构介绍',
    didian: '实体介绍',
    location: '实体介绍',
    shijian: '事件介绍',
    event: '事件介绍'
  }
  return labels[type] || '实体介绍'
}

export function mapKnowledgeDetailAttribute(attr) {
  return {
    id: attr.attributeId,
    code: attr.code || '',
    label: attr.label || '',
    value: attr.value || '',
    valueRange: attr.valueRange || '',
    imageUrls: ensureHttpsUrls(attr.imageUrls || []),
    resourceLinks: mapAttributeResourceLinks(attr.resourceLinks)
  }
}

function mapAttributeResourceLinks(links) {
  if (!Array.isArray(links) || !links.length) {
    return []
  }
  return links
    .map((item) => {
      const resourceId = Number(item && (item.resourceId || item.id))
      if (!Number.isFinite(resourceId) || resourceId <= 0) {
        return null
      }
      return {
        resourceId,
        title: (item && (item.title || item.name)) || String(resourceId)
      }
    })
    .filter(Boolean)
}

function isMediaAttribute(attr) {
  const range = attr.valueRange || ''
  return ['image', 'image_set', 'video_urls', 'audio_urls'].includes(range)
}

export function mapIdentityFacets(identities, fallback) {
  if (identities && identities.length) {
    return mergePersonIdentityFacets(
      identities.map((item) => ({
        key: normalizeTypeKey(item.key),
        id: item.id,
        label: item.label,
        count: item.count || 0
      }))
    )
  }
  return (fallback || []).map((item) => ({ ...item, count: 0 }))
}

/** 兼容未升级后端：前台兜底将「人物」「荣氏人物」合并为「人物」 */
function mergePersonIdentityFacets(items) {
  const PERSON_LABELS = ['人物', '荣氏人物']
  const personItems = items.filter((item) => PERSON_LABELS.includes(item.label))
  if (personItems.length <= 1) {
    return items.map((item) => (
      item.label === '荣氏人物' ? { ...item, label: '人物' } : item
    ))
  }

  const primary = personItems.find((item) => item.label === '人物') || personItems[0]
  const mergedPerson = {
    ...primary,
    label: '人物',
    count: personItems.reduce((sum, item) => sum + (item.count || 0), 0)
  }

  const result = []
  let inserted = false
  items.forEach((item) => {
    if (PERSON_LABELS.includes(item.label)) {
      if (!inserted) {
        result.push(mergedPerson)
        inserted = true
      }
      return
    }
    result.push(item)
  })
  return result
}

export function mapAttributeOptions(list) {
  return (list || []).map((item) => ({
    label: item.label,
    value: item.key
  }))
}

export function fetchGraphIdentityOptions() {
  return request({
    url: '/pc/knowledge/graph/identity-options',
    method: 'get'
  }).then(unwrapData)
}

export function suggestGraphEntities(keyword, limit = 20) {
  return request({
    url: '/pc/knowledge/graph/entity-suggest',
    method: 'get',
    params: { keyword, limit }
  }).then(unwrapData)
}

export function queryHomeGraph(payload) {
  return request({
    url: '/pc/knowledge/graph/query',
    method: 'post',
    data: payload
  }).then(unwrapData)
}

function normalizeTypeKey(code) {
  if (!code) {
    return ''
  }
  return String(code).trim().toLowerCase()
}
